import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Barcode, Minus, Plus, Search, ShoppingCart, Trash2, WifiOff, X,
} from 'lucide-react'
import {
  calcSaleTotals, formatMoney, formatNumber, saleSchema,
  type PaymentMethod, type SaleItemInput,
} from '@najah/core'
import { Badge, Button, EmptyState, FullPageSpinner, Input, Spinner, useToast } from '@najah/ui'
import { getPosDataSource, type PosProduct } from '../lib/offline/posData'
import { shiftsApi } from '../lib/salesApi'
import { useAuth } from '../lib/auth'
import { ShiftGate } from '../components/pos/ShiftGate'
import { PaymentDialog } from '../components/pos/PaymentDialog'
import { ReceiptDialog, type ReceiptData } from '../components/pos/ReceiptDialog'
import { ApiError } from '../lib/api'

interface CartLine extends SaleItemInput {
  /** الرصيد المتاح وقت الإضافة — للتحذير فقط، الخادم هو الحَكَم */
  available: number
  trackStock: boolean
}

export function PosPage() {
  const { user } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const source = getPosDataSource()

  const [cart, setCart] = useState<CartLine[]>([])
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<PosProduct[]>([])
  const [searching, setSearching] = useState(false)
  const [payOpen, setPayOpen] = useState(false)
  const [receipt, setReceipt] = useState<ReceiptData | null>(null)
  const scanRef = useRef<HTMLInputElement>(null)

  const { data: shiftData, isPending } = useQuery({ queryKey: ['shift'], queryFn: shiftsApi.current })

  const totals = calcSaleTotals(cart)

  /* ── السلة ── */
  const addProduct = useCallback((p: PosProduct) => {
    setCart((prev) => {
      const existing = prev.find((l) => l.productId === p.id)
      if (existing) {
        return prev.map((l) => (l.productId === p.id ? { ...l, quantity: l.quantity + 1 } : l))
      }
      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          productId: p.id,
          nameSnapshot: p.nameAr,
          skuSnapshot: p.sku,
          unitSnapshot: p.unit,
          costSnapshot: p.costPrice,
          unitPrice: p.retailPrice,
          quantity: 1,
          discount: 0,
          taxRate: p.taxRate,
          available: p.quantity,
          trackStock: p.trackStock,
        },
      ]
    })
    setTerm('')
    setResults([])
    scanRef.current?.focus()
  }, [])

  const setQuantity = (lineId: string, quantity: number) => {
    if (quantity <= 0) {
      setCart((prev) => prev.filter((l) => l.id !== lineId))
      return
    }
    setCart((prev) => prev.map((l) => (l.id === lineId ? { ...l, quantity } : l)))
  }

  /* ── البحث ── */
  useEffect(() => {
    if (term.trim().length < 2) {
      setResults([])
      return
    }
    let cancelled = false
    setSearching(true)
    // تأخير بسيط: قارئ الباركود يكتب الرمز كاملاً في أجزاء من الثانية،
    // فلا نريد استعلاماً عند كل حرف
    const timer = setTimeout(async () => {
      try {
        const found = await source.search(term.trim())
        if (!cancelled) setResults(found)
      } catch {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 220)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [term, source])

  async function handleScan(code: string) {
    const trimmed = code.trim()
    if (!trimmed) return
    const product = await source.findByBarcode(trimmed)
    if (product) {
      addProduct(product)
    } else {
      // ليس باركوداً معروفاً: نتركه كنصّ بحث بدل ابتلاعه بصمت
      toast.error('لا يوجد منتج بهذا الرمز', trimmed)
    }
  }

  /* ── إتمام البيع ── */
  const submitMutation = useMutation({
    mutationFn: async ({ payments, customerId }: {
      payments: { method: PaymentMethod; amount: number }[]
      customerId: string | null
    }) => {
      const reference = await source.nextReference()
      const occurredAt = new Date().toISOString()

      const payload = {
        id: crypto.randomUUID(),
        reference,
        items: cart.map(({ available: _available, trackStock: _trackStock, ...item }) => item),
        payments: payments.map((p) => ({ id: crypto.randomUUID(), method: p.method, amount: p.amount })),
        shiftId: shiftData?.shift?.id ?? null,
        registerId: shiftData?.shift?.registerId ?? null,
        customerId,
        occurredAt,
        total: totals.total,
      }

      // نتحقّق بنفس مخطط الخادم قبل الإرسال — خطأ يُكتشف هنا أفضل من فاتورة عالقة في الطابور
      const parsed = saleSchema.safeParse(payload)
      if (!parsed.success) {
        throw new ApiError(400, parsed.error.issues[0]?.message ?? 'بيانات الفاتورة غير صالحة', 'VALIDATION_ERROR')
      }

      const result = await source.submitSale(parsed.data)
      const paid = payments.reduce((sum, p) => sum + p.amount, 0)

      return {
        reference,
        occurredAt,
        queued: result.queued,
        paid,
        change: Math.max(0, paid - totals.total),
      }
    },
    onSuccess: (res) => {
      setReceipt({
        reference: res.reference,
        occurredAt: res.occurredAt,
        items: cart.map((l) => ({
          nameSnapshot: l.nameSnapshot,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          unitSnapshot: l.unitSnapshot,
          lineTotal: l.unitPrice * l.quantity - l.discount,
        })),
        subtotal: totals.subtotal,
        discountTotal: totals.discountTotal,
        taxTotal: totals.taxTotal,
        total: totals.total,
        paid: res.paid,
        change: res.change,
        cashierName: user?.fullName ?? '',
        storeName: user?.storeName ?? 'مكتبة النجاح',
        queued: res.queued,
      })
      setCart([])
      setPayOpen(false)
      void queryClient.invalidateQueries({ queryKey: ['shift'] })
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      if (res.queued) toast.toast('warning', 'حُفظت محلياً', 'ستُرسل عند عودة الاتصال')
      else toast.success('تمّ البيع', res.reference)
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'تعذّر إتمام البيع'
      toast.error('لم تُحفظ الفاتورة', message)
    },
  })

  if (isPending) return <FullPageSpinner label="جارٍ فتح نقطة البيع…" />
  if (!shiftData?.shift) return <ShiftGate registers={shiftData?.registers ?? []} />

  const shift = shiftData.shift

  return (
    <div className="mx-auto max-w-[1600px]">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">نقطة البيع</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            {shift.registerName} · {formatNumber(shift.salesCount)} فاتورة ·{' '}
            <span dir="ltr">{formatMoney(shift.salesTotal)}</span>
          </p>
        </div>
        {!source.offlineCapable && (
          <Badge tone="warning" dot>
            <WifiOff className="size-3.5" />
            وضع المتصفّح — لا يعمل دون اتصال
          </Badge>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_400px]">
        {/* ── البحث والنتائج ── */}
        <div className="space-y-3">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void handleScan(term)
            }}
          >
            <Input
              ref={scanRef}
              autoFocus
              placeholder="امسح الباركود أو ابحث بالاسم…"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              startIcon={<Barcode className="size-5" />}
              endIcon={
                searching ? (
                  <Spinner className="size-4" />
                ) : term ? (
                  <button
                    type="button"
                    onClick={() => { setTerm(''); setResults([]) }}
                    className="pointer-events-auto rounded p-0.5 text-fg-subtle hover:text-fg"
                    aria-label="مسح"
                  >
                    <X className="size-4" />
                  </button>
                ) : undefined
              }
              className="h-14 text-base"
            />
          </form>

          <div className="rounded-xl border border-border bg-surface">
            {results.length > 0 ? (
              <ul className="divide-y divide-border">
                {results.map((p) => {
                  const out = p.trackStock && p.quantity <= 0
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => addProduct(p)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-start transition-colors hover:bg-surface-hover"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-fg">{p.nameAr}</p>
                          <p className="font-mono text-xs text-fg-subtle" dir="ltr">{p.sku}</p>
                        </div>
                        {p.trackStock && (
                          <Badge tone={out ? 'danger' : p.quantity <= 5 ? 'warning' : 'neutral'}>
                            {formatNumber(p.quantity)} {p.unit}
                          </Badge>
                        )}
                        <span className="font-semibold tabular-nums text-fg" dir="ltr">
                          {formatMoney(p.retailPrice)}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <EmptyState
                icon={<Search className="size-10" />}
                title={term.length >= 2 ? 'لا توجد نتائج' : 'ابحث عن منتج'}
                description={
                  term.length >= 2
                    ? 'جرّب اسماً آخر أو امسح الباركود'
                    : 'امسح الباركود بالقارئ، أو اكتب حرفين على الأقل'
                }
              />
            )}
          </div>
        </div>

        {/* ── السلة ── */}
        <div className="flex max-h-[calc(100dvh-12rem)] flex-col rounded-xl border border-border bg-surface lg:sticky lg:top-20">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="flex items-center gap-2 font-semibold text-fg">
              <ShoppingCart className="size-4" />
              السلة
              {cart.length > 0 && <Badge tone="primary">{formatNumber(cart.length)}</Badge>}
            </h2>
            {cart.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setCart([])} className="text-danger">
                إفراغ
              </Button>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {cart.length === 0 ? (
              <EmptyState icon={<ShoppingCart className="size-9" />} title="السلة فارغة" description="امسح منتجاً للبدء" />
            ) : (
              <ul className="divide-y divide-border">
                {cart.map((line) => {
                  const over = line.trackStock && line.quantity > line.available
                  return (
                    <li key={line.id} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-fg">{line.nameSnapshot}</p>
                          <p className="text-xs text-fg-subtle" dir="ltr">
                            {formatMoney(line.unitPrice)} / {line.unitSnapshot}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setQuantity(line.id, 0)}
                          className="rounded p-1 text-fg-subtle transition-colors hover:bg-danger-subtle hover:text-danger"
                          aria-label="حذف"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>

                      <div className="mt-2 flex items-center justify-between gap-2">
                        <div className="flex items-center rounded-lg border border-border">
                          <button
                            type="button"
                            onClick={() => setQuantity(line.id, line.quantity - 1)}
                            className="px-2.5 py-1.5 text-fg-muted transition-colors hover:bg-surface-hover"
                            aria-label="إنقاص"
                          >
                            <Minus className="size-3.5" />
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={line.quantity}
                            onChange={(e) => setQuantity(line.id, Number(e.target.value) || 1)}
                            className="w-12 border-x border-border bg-transparent py-1.5 text-center text-sm tabular-nums text-fg focus:outline-none"
                            dir="ltr"
                            aria-label="الكمية"
                          />
                          <button
                            type="button"
                            onClick={() => setQuantity(line.id, line.quantity + 1)}
                            className="px-2.5 py-1.5 text-fg-muted transition-colors hover:bg-surface-hover"
                            aria-label="زيادة"
                          >
                            <Plus className="size-3.5" />
                          </button>
                        </div>

                        <span className="font-semibold tabular-nums text-fg" dir="ltr">
                          {formatMoney(line.unitPrice * line.quantity)}
                        </span>
                      </div>

                      {over && (
                        <p className="mt-1.5 text-xs text-warning">
                          المتوفّر <span dir="ltr">{formatNumber(line.available)}</span> فقط — قد يُرفض البيع
                        </p>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div className="border-t border-border p-4">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-fg-muted">
                <span>المجموع</span>
                <span dir="ltr">{formatMoney(totals.subtotal)}</span>
              </div>
              {totals.taxTotal > 0 && (
                <div className="flex justify-between text-fg-muted">
                  <span>الضريبة</span>
                  <span dir="ltr">{formatMoney(totals.taxTotal)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-border pt-2 text-lg font-bold text-fg">
                <span>الإجمالي</span>
                <span dir="ltr">{formatMoney(totals.total)}</span>
              </div>
            </div>

            <Button
              size="lg"
              fullWidth
              className="mt-3 h-14 text-base"
              disabled={cart.length === 0}
              onClick={() => setPayOpen(true)}
            >
              الدفع · <span dir="ltr">{formatMoney(totals.total)}</span>
            </Button>
          </div>
        </div>
      </div>

      <PaymentDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        total={totals.total}
        submitting={submitMutation.isPending}
        onConfirm={(payments, customerId) => submitMutation.mutate({ payments, customerId })}
      />

      <ReceiptDialog open={!!receipt} onClose={() => setReceipt(null)} receipt={receipt} />
    </div>
  )
}
